// Importing the package directly breaks the build in vite+rollup prod build,
// like for vuedraggable (see PagesList.vue); this resolves to the same module
// instance vuedraggable uses, which is what Sortable.mount must be called on.
import Sortable from "sortablejs";

/**
 * The subset of a SortableJS plugin-hook context this module reads
 * (SortableJS ships no types). `sortable` is the instance the drag started
 * in; `revertOnSpill` is that instance's OnSpill plugin, present when the
 * sortable sets `revertOnSpill: true`; `dragEl` is the dragged element —
 * SortableJS's module-level one, which its drop handler nulls once a drag
 * is fully processed.
 */
interface SortableDropContext {
  dragEl?: unknown;
  originalEvent?: Event;
  sortable?: {
    revertOnSpill?: { onSpill: (context: SortableDropContext) => void };
  };
}

/**
 * A SortableJS plugin canceling a drag on Escape: pressing Escape aborts the
 * browser's native drag, which surfaces as a bare `dragend` (no `drop`
 * event) — but SortableJS still applies the sort at the last hovered
 * position. When the drop hook sees such an end, this plugin puts the
 * element back where the drag started (reusing the OnSpill revert
 * machinery), so the indices SortableJS recomputes right after show no move
 * and no change event reaches vuedraggable. A release outside any list
 * surfaces the same way and is reverted too, consistent with
 * `revertOnSpill`.
 *
 * Opt in per sortable with `cancelOnEscape: true`, alongside
 * `revertOnSpill: true` (the revert machinery must be initialized by the
 * sortable itself: plugin defaults merge too late for the OnSpill plugin's
 * own initialization).
 */
export function cancelOnEscapePlugin() {
  function CancelOnEscape() {}
  CancelOnEscape.prototype = {
    drop(context: SortableDropContext) {
      if (context.originalEvent?.type !== "dragend") return;
      // With nested sortables one drag can reach several instances' drop
      // handlers: the first one nulls SortableJS's `dragEl` when it
      // finishes, so a later (stale) hook must not revert — OnSpill would
      // crash re-inserting a null element, aborting SortableJS's cleanup
      // mid-way and corrupting the next drag.
      if (!(context.dragEl instanceof Element)) return;
      context.sortable?.revertOnSpill?.onSpill(context);
    },
  };
  return Object.assign(CancelOnEscape, { pluginName: "cancelOnEscape" });
}

let mounted = false;

/** Mount {@link cancelOnEscapePlugin} once (re-mounting throws). */
export function mountSortableCancelOnEscape(): void {
  if (mounted) return;
  try {
    Sortable.mount(cancelOnEscapePlugin());
  } catch {
    // Already mounted on the shared Sortable module: the flag above lives
    // in this module, which the dev server's HMR can re-evaluate.
  }
  mounted = true;
}
